import { apiFetch } from "./client.ts";

export interface Page<T> {
  items: T[];
  total: number;
  limit: number;
  offset: number;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function qs(params: Record<string, any>): string {
  const s = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v != null && v !== "") s.set(k, String(v));
  }
  const r = s.toString();
  return r ? `?${r}` : "";
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface MarketingSource {
  id: string; name: string; type: string; enabled: boolean;
  config: Record<string, unknown>; lastRunAt: string | null;
  createdAt: string; updatedAt: string; version: number;
}

export interface DiscoveredCompany {
  id: string; name: string; domain: string; industry: string | null;
  size: string | null; location: string | null; status: string;
  score: number | null; sourceId: string; createdAt: string; updatedAt: string;
}

export interface CompanyResearch {
  id: string; discoveredCompanyId: string; description: string | null;
  products: unknown[]; techStack: string[]; teamSize: string | null;
  fundingStage: string | null; annualRevenue: string | null;
  socialProfiles: Record<string, string>; createdAt: string;
}

export interface WebsiteAnalysis {
  id: string; discoveredCompanyId: string; url: string;
  services: string[]; techStack: string[]; opportunities: string[];
  aiAutomationOpportunities: string[]; performanceScore: number | null;
  seoScore: number | null; mobileScore: number | null; analyzedBy: string | null;
  createdAt: string;
}

export interface Campaign {
  id: string; name: string; description: string | null; type: string;
  status: string; goals: Record<string, unknown> | null;
  targetIndustries: string[]; targetCompanySizes: string[];
  startedAt: string | null; endedAt: string | null;
  createdAt: string; updatedAt: string; version: number;
}

export interface MarketingContent {
  id: string; campaignId: string | null; type: string; status: string;
  title: string; body: string | null; meta: Record<string, unknown> | null;
  generatedBy: string | null; publishedAt: string | null;
  createdAt: string; updatedAt: string; version: number;
}

export interface ImagePrompt {
  id: string; contentId: string | null; campaignId: string | null;
  prompt: string; style: string | null; aspectRatio: string | null;
  negativePrompt: string | null; generatedBy: string | null;
  createdAt: string;
}

export interface SocialPost {
  id: string; contentId: string | null; campaignId: string | null;
  platform: string; status: string; body: string;
  mediaUrls: string[]; scheduledAt: string | null;
  publishedAt: string | null; generatedBy: string | null;
  createdAt: string; updatedAt: string; version: number;
}

export interface SeoKeyword {
  id: string; keyword: string; searchVolume: number | null;
  difficulty: number | null; cpc: number | null; intent: string | null;
  clusterId: string | null; campaignId: string | null;
  createdAt: string;
}

export interface SeoTopic {
  id: string; campaignId: string | null; title: string;
  pillarKeyword: string; supportingKeywords: string[]; status: string;
  suggestedContent: Array<{ title: string; type: string }>;
  internalLinking: Record<string, string[]>;
  createdAt: string; updatedAt: string;
}

export interface OutreachDraft {
  id: string; leadId: string; campaignId: string | null;
  status: string; subject: string; body: string;
  fromName: string | null; fromEmail: string | null;
  toName: string | null; toEmail: string | null;
  personalizationData: Record<string, unknown> | null;
  generatedBy: string | null; approvedBy: string | null;
  approvedAt: string | null; sentAt: string | null;
  createdAt: string; updatedAt: string; version: number;
}

export interface MarketingOverview {
  discovery: { total: number; qualified: number; pendingResearch: number; websites: number };
  content: { total: number; draft: number; approved: number; published: number };
  social: { total: number };
  campaigns: { total: number };
  outreach: { total: number; draft: number; pendingApproval: number; approved: number };
}

// ---------------------------------------------------------------------------
// Analytics
// ---------------------------------------------------------------------------

export async function getMarketingOverview(token: string): Promise<MarketingOverview> {
  return apiFetch<MarketingOverview>("/api/v1/marketing/analytics/overview", { token });
}

// ---------------------------------------------------------------------------
// Discovery
// ---------------------------------------------------------------------------

export async function listSources(token: string, p: { limit?: number; offset?: number } = {}): Promise<Page<MarketingSource>> {
  return apiFetch<Page<MarketingSource>>(`/api/v1/marketing/discovery/sources${qs(p)}`, { token });
}

export async function listDiscovered(token: string, p: { status?: string; limit?: number; offset?: number } = {}): Promise<Page<DiscoveredCompany>> {
  return apiFetch<Page<DiscoveredCompany>>(`/api/v1/marketing/discovery/discovered${qs(p)}`, { token });
}

// ---------------------------------------------------------------------------
// Research
// ---------------------------------------------------------------------------

export async function listResearch(token: string, p: { discoveredCompanyId?: string; limit?: number; offset?: number } = {}): Promise<Page<CompanyResearch>> {
  return apiFetch<Page<CompanyResearch>>(`/api/v1/marketing/research${qs(p)}`, { token });
}

export async function listAnalyses(token: string, p: { discoveredCompanyId?: string; limit?: number; offset?: number } = {}): Promise<Page<WebsiteAnalysis>> {
  return apiFetch<Page<WebsiteAnalysis>>(`/api/v1/marketing/research/analyses${qs(p)}`, { token });
}

// ---------------------------------------------------------------------------
// Content
// ---------------------------------------------------------------------------

export async function listContent(token: string, p: { campaignId?: string; type?: string; status?: string; limit?: number; offset?: number } = {}): Promise<Page<MarketingContent>> {
  return apiFetch<Page<MarketingContent>>(`/api/v1/marketing/content${qs(p)}`, { token });
}

export async function createContent(token: string, data: { type: string; title: string; body?: string; campaignId?: string; meta?: Record<string, unknown> }): Promise<MarketingContent> {
  return apiFetch<MarketingContent>("/api/v1/marketing/content", { token, method: "POST", body: JSON.stringify(data) });
}

export async function transitionContent(token: string, id: string, status: string, version: number): Promise<MarketingContent> {
  return apiFetch<MarketingContent>(`/api/v1/marketing/content/${id}/transition`, { token, method: "POST", body: JSON.stringify({ status, version }) });
}

export async function listImagePrompts(token: string, p: { contentId?: string; campaignId?: string; limit?: number; offset?: number } = {}): Promise<Page<ImagePrompt>> {
  return apiFetch<Page<ImagePrompt>>(`/api/v1/marketing/content/image-prompts${qs(p)}`, { token });
}

// ---------------------------------------------------------------------------
// Campaigns
// ---------------------------------------------------------------------------

export async function listCampaigns(token: string, p: { type?: string; status?: string; limit?: number; offset?: number } = {}): Promise<Page<Campaign>> {
  return apiFetch<Page<Campaign>>(`/api/v1/marketing/campaigns${qs(p)}`, { token });
}

export async function createCampaign(token: string, data: { name: string; type: string; description?: string }): Promise<Campaign> {
  return apiFetch<Campaign>("/api/v1/marketing/campaigns", { token, method: "POST", body: JSON.stringify(data) });
}

export async function transitionCampaign(token: string, id: string, status: string, version: number): Promise<Campaign> {
  return apiFetch<Campaign>(`/api/v1/marketing/campaigns/${id}/transition`, { token, method: "POST", body: JSON.stringify({ status, version }) });
}

// ---------------------------------------------------------------------------
// Social
// ---------------------------------------------------------------------------

export async function listSocialPosts(token: string, p: { campaignId?: string; platform?: string; status?: string; limit?: number; offset?: number } = {}): Promise<Page<SocialPost>> {
  return apiFetch<Page<SocialPost>>(`/api/v1/marketing/social${qs(p)}`, { token });
}

export async function transitionSocialPost(token: string, id: string, status: string, version: number): Promise<SocialPost> {
  return apiFetch<SocialPost>(`/api/v1/marketing/social/${id}/transition`, { token, method: "POST", body: JSON.stringify({ status, version }) });
}

// ---------------------------------------------------------------------------
// SEO
// ---------------------------------------------------------------------------

export async function listKeywords(token: string, p: { campaignId?: string; intent?: string; limit?: number; offset?: number } = {}): Promise<Page<SeoKeyword>> {
  return apiFetch<Page<SeoKeyword>>(`/api/v1/marketing/seo/keywords${qs(p)}`, { token });
}

export async function listTopics(token: string, p: { campaignId?: string; status?: string; limit?: number; offset?: number } = {}): Promise<Page<SeoTopic>> {
  return apiFetch<Page<SeoTopic>>(`/api/v1/marketing/seo/topics${qs(p)}`, { token });
}

// ---------------------------------------------------------------------------
// Outreach
// ---------------------------------------------------------------------------

export async function listOutreach(token: string, p: { status?: string; leadId?: string; limit?: number; offset?: number } = {}): Promise<Page<OutreachDraft>> {
  return apiFetch<Page<OutreachDraft>>(`/api/v1/marketing/outreach${qs(p)}`, { token });
}

export async function requestOutreachApproval(token: string, id: string, version: number): Promise<OutreachDraft> {
  return apiFetch<OutreachDraft>(`/api/v1/marketing/outreach/${id}/request-approval`, { token, method: "POST", body: JSON.stringify({ version }) });
}

export async function approveOutreach(token: string, id: string, version: number): Promise<OutreachDraft> {
  return apiFetch<OutreachDraft>(`/api/v1/marketing/outreach/${id}/approve`, { token, method: "POST", body: JSON.stringify({ version }) });
}

export async function rejectOutreach(token: string, id: string, version: number): Promise<OutreachDraft> {
  return apiFetch<OutreachDraft>(`/api/v1/marketing/outreach/${id}/reject`, { token, method: "POST", body: JSON.stringify({ version }) });
}
